import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { MessageSquare, Send, Search, BookOpen, Image as ImageIcon, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const FEATURES = [
  { key: "help", label: "/help", icon: MessageSquare, color: "#0EA5E9" },
  { key: "dm", label: "DM chat", icon: Send, color: "#f59e0b" },
  { key: "check", label: "Code checks", icon: Search, color: "#f59e0b" },
  { key: "explain", label: "Explain", icon: BookOpen, color: "#a855f7" },
  { key: "screenshots", label: "Screenshots", icon: ImageIcon, color: "#ec4899" },
  { key: "files", label: "File uploads", icon: FileText, color: "#60a5fa" },
];

const Totals = ({ u }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
    {FEATURES.map((f) => (
      <div key={f.key} data-testid={`ai-usage-total-${f.key}`} className="bg-black/30 border border-zinc-800 rounded-lg p-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500"><f.icon className="w-3.5 h-3.5" style={{ color: f.color }} />{f.label}</div>
        <div className="font-head text-2xl font-semibold mt-1">{u.totals[f.key].count.toLocaleString()}</div>
        <div className="text-xs text-zinc-500">{u.totals[f.key].users.toLocaleString()} unique users</div>
      </div>
    ))}
  </div>
);

const Servers = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="ai-usage-servers">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
        <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Server</th>
        {FEATURES.map((f) => <th key={f.key} className="py-2 pr-3 text-right">{f.label}</th>)}
        <th className="py-2 pr-3 text-right">Total</th><th className="py-2 text-right">Users</th>
      </tr></thead>
      <tbody>
        {rows.map((g, i) => (
          <tr key={g.guild_id || "dm"} data-testid={`ai-usage-server-${i}`} className="border-b border-zinc-800/60 hover:bg-white/[0.02]">
            <td className="py-2 pr-3 text-zinc-500">{i + 1}</td>
            <td className="py-2 pr-3 max-w-[220px] truncate" title={g.guild_id}>{g.name}</td>
            {FEATURES.map((f) => <td key={f.key} className={`py-2 pr-3 text-right font-mono ${g[f.key] ? "" : "text-zinc-600"}`}>{g[f.key]}</td>)}
            <td className="py-2 pr-3 text-right font-mono font-semibold">{g.total}</td>
            <td className="py-2 text-right font-mono">{g.users}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-zinc-500">No AI usage in this period yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

export const AdminAiUsage = () => {
  const [days, setDays] = useState(30);
  const [u, setU] = useState(null);
  useEffect(() => {
    setU(null);
    api.get(`/admin/ai-help/usage?days=${days}`).then((r) => setU(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [days]);
  return (
    <div data-testid="ai-usage-panel" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h3 className="font-head font-semibold">AI feature usage</h3>
          <p className="text-xs text-zinc-500">Successful requests{u ? ` · ${u.requests.toLocaleString()} total · ${u.unique_users.toLocaleString()} unique users` : ""}</p>
        </div>
        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`ai-usage-days-${d}`} onClick={() => setDays(d)}
              className={`text-xs rounded-md px-3 py-1.5 transition-colors ${days === d ? "bg-volt text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>{d}d</button>
          ))}
        </div>
      </div>
      {!u ? <p className="text-sm text-zinc-500">Loading…</p> : <>
        <Totals u={u} />
        <div className="h-56" data-testid="ai-usage-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={u.daily}>
              <XAxis dataKey="day" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 10 }} width={30} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "rgb(var(--z-900))", border: "1px solid rgb(var(--z-800))", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {FEATURES.slice(0, 4).map((f) => <Bar key={f.key} dataKey={f.key} name={f.label} stackId="a" fill={f.color} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <h4 className="text-sm font-medium mb-2">Top servers</h4>
          <Servers rows={u.servers} />
        </div>
      </>}
    </div>
  );
};
